import { useState, useMemo, useEffect } from 'react';
import * as Yup from 'yup';

import { fundOptions } from '../../../defaultFilters';

const fundingStageTypeOptions = [
  {
    label: 'Any',
    value: null,
  },
  {
    label: 'Has had at least',
    value: 'early',
  },
  {
    label: 'Has experienced growth',
    value: 'growth',
  },
  {
    label: 'Currently at a company in',
    value: 'current',
  },
];

const yearsOptions = [
  {
    label: '2 years',
    value: 2,
  },
  {
    label: '5 years',
    value: 5,
  },
  {
    label: '10 years',
    value: 10,
  },
];

// Helper function to parse incoming filter values
const parseFilterValue = (filters) => {
  // Check which filter key has data
  const companyStages = filters?.companyStages || [];
  const stageTransitions = filters?.stageTransitions || [];
  const activePositionsOrganizationStages = filters?.activePositionsOrganizationStages || [];

  let activeFilter = null;
  let filterData = null;

  if (companyStages.length > 0) {
    activeFilter = 'early';
    filterData = companyStages[0];
  } else if (stageTransitions.length > 0) {
    activeFilter = 'growth';
    filterData = stageTransitions[0];
  } else if (activePositionsOrganizationStages.length > 0) {
    activeFilter = 'current';
    filterData = activePositionsOrganizationStages[0];
  }

  if (!activeFilter || !filterData) {
    return {
      fundingStageType: fundingStageTypeOptions[0],
      years: yearsOptions[0],
      stageFrom: fundOptions[0],
      stageTo: fundOptions[1],
    };
  }

  const fundingStageType = fundingStageTypeOptions.find((opt) => opt.value === activeFilter);

  // For 'early' type: from = years, to = stage
  // For 'growth' and 'current': from/to are stages
  let years = yearsOptions[0];
  let stageFrom = fundOptions[0];
  let stageTo = fundOptions[1];

  if (activeFilter === 'early') {
    // filterData.from is the years object
    if (filterData.from) {
      if (typeof filterData.from === 'object' && filterData.from.value) {
        years = yearsOptions.find((opt) => opt.value === filterData.from.value) || filterData.from;
      } else {
        years = yearsOptions.find((opt) => opt.value === filterData.from) || yearsOptions[0];
      }
    }
    // filterData.to is the stage object
    if (filterData.to) {
      if (typeof filterData.to === 'object' && filterData.to.value) {
        stageFrom = fundOptions.find((opt) => opt.value === filterData.to.value) || filterData.to;
      } else {
        stageFrom = fundOptions.find((opt) => opt.value === filterData.to) || fundOptions[0];
      }
    }
  } else if (activeFilter === 'growth') {
    // Both from and to are stage objects
    if (filterData.from) {
      if (typeof filterData.from === 'object' && filterData.from.value) {
        stageFrom = fundOptions.find((opt) => opt.value === filterData.from.value) || filterData.from;
      } else {
        stageFrom = fundOptions.find((opt) => opt.value === filterData.from) || fundOptions[0];
      }
    }
    if (filterData.to) {
      if (typeof filterData.to === 'object' && filterData.to.value) {
        stageTo = fundOptions.find((opt) => opt.value === filterData.to.value) || filterData.to;
      } else {
        stageTo = fundOptions.find((opt) => opt.value === filterData.to) || fundOptions[1];
      }
    }
  } else if (activeFilter === 'current') {
    // from is the stage object
    if (filterData.from) {
      if (typeof filterData.from === 'object' && filterData.from.value) {
        stageFrom = fundOptions.find((opt) => opt.value === filterData.from.value) || filterData.from;
      } else {
        stageFrom = fundOptions.find((opt) => opt.value === filterData.from) || fundOptions[0];
      }
    }
  }

  return {
    fundingStageType,
    years,
    stageFrom,
    stageTo,
  };
};

export const useCompanyFundingStageFilter = ({ setFilters, filters }) => {
  const initialValues = useMemo(() => parseFilterValue(filters), [filters]);

  const [fundingStageType, setFundingStageType] = useState(initialValues.fundingStageType);
  const [years, setYears] = useState(initialValues.years);
  const [stageFrom, setStageFrom] = useState(initialValues.stageFrom);
  const [stageTo, setStageTo] = useState(initialValues.stageTo);

  // Sync local state when filters prop changes
  useEffect(() => {
    const parsed = parseFilterValue(filters);
    setFundingStageType(parsed.fundingStageType);
    setYears(parsed.years);
    setStageFrom(parsed.stageFrom);
    setStageTo(parsed.stageTo);
  }, [filters]);

  const formSchema = Yup.object().shape({
    fundingStageType: Yup.object()
      .shape({
        label: Yup.string().required(),
        value: Yup.string().nullable(),
      })
      .nullable(),
    years: Yup.object()
      .shape({
        label: Yup.string(),
        value: Yup.number(),
      })
      .nullable(),
    stageFrom: Yup.object()
      .shape({
        label: Yup.string(),
        value: Yup.string(),
        index: Yup.number(),
      })
      .nullable(),
    stageTo: Yup.object()
      .shape({
        label: Yup.string(),
        value: Yup.string(),
        index: Yup.number(),
      })
      .nullable(),
  });

  const onSubmit = ({ fundingStageType: type, years: selectedYears, stageFrom: from, stageTo: to }) => {
    if (!type?.value) {
      // Clear filters if no type selected
      setFilters({
        companyStages: [],
        stageTransitions: [],
        activePositionsOrganizationStages: [],
      });
      return;
    }

    const stage = type.value;
    let key = stage === 'current' ? 'activePositionsOrganizationStages' : 'stageTransitions';
    if (stage === 'early') {
      key = 'companyStages';
    }

    // Build the filter value object matching the expected structure
    const filterItem = {
      stage,
      key: '', // Will be set later
    };

    // For 'early' type: from = years object, to = stage object
    if (stage === 'early') {
      filterItem.from = selectedYears; // Years object: { label: '2 years', value: 2 }
      filterItem.to = from; // Stage object: { label: 'Seed', value: 'seed' }
      filterItem.key = JSON.stringify({ stage, from: selectedYears, to: from });
    }

    // For 'growth' type: from and to are stage objects
    if (stage === 'growth') {
      filterItem.from = from;
      filterItem.to = to;
      filterItem.key = JSON.stringify({ stage, from, to });
    }

    // For 'current' type: only from (stage object)
    if (stage === 'current') {
      filterItem.from = from;
      filterItem.key = JSON.stringify({ stage, from });
    }

    const filterValue = [filterItem];

    // Clear other stage-related filters
    const filtersToUpdate = {
      companyStages: [],
      stageTransitions: [],
      activePositionsOrganizationStages: [],
      [key]: filterValue,
    };

    setFilters(filtersToUpdate);
  };

  const onFundingStageTypeChange = (option) => {
    // Compute reset values purely from the incoming option (no hook state reads)
    let resetValues;
    if (option?.value === 'early') {
      resetValues = {
        years: yearsOptions[0],
        stageFrom: fundOptions[0],
        stageTo: null,
      };
    } else if (option?.value === 'growth') {
      resetValues = {
        years: null,
        stageFrom: fundOptions[0],
        stageTo: fundOptions[1],
      };
    } else if (option?.value === 'current') {
      resetValues = {
        years: null,
        stageFrom: fundOptions[0],
        stageTo: null,
      };
    } else {
      // If 'Select' option (no value)
      resetValues = {
        years: yearsOptions[0],
        stageFrom: fundOptions[0],
        stageTo: fundOptions[1],
      };
    }

    // Update hook state with computed values
    setFundingStageType(option);
    setYears(resetValues.years);
    setStageFrom(resetValues.stageFrom);
    setStageTo(resetValues.stageTo);

    // Return computed values so component can update Formik deterministically
    return resetValues;
  };

  const onStageFromChange = (option, currentFundingStageType, currentStageTo) => {
    // Compute adjusted stageTo purely from parameters (no hook state reads)
    let adjustedStageTo = null;

    // For growth type, ensure stageTo is always after stageFrom
    if (currentFundingStageType?.value === 'growth' && currentStageTo && currentStageTo.index <= option.index) {
      const nextStage = fundOptions.find((item) => item.index > option.index);
      if (nextStage) {
        adjustedStageTo = nextStage;
        // Update hook state with adjusted value
        setStageTo(nextStage);
      }
    }

    // Update hook state with new stageFrom
    setStageFrom(option);

    // Return the adjusted stageTo (or null if no adjustment) so component can update Formik
    return adjustedStageTo;
  };

  const getAvailableStageToOptions = (currentFundingStageType, currentStageFrom) => {
    if (currentFundingStageType?.value !== 'growth' || !currentStageFrom) {
      return fundOptions;
    }
    return fundOptions.filter((option) => option.index > currentStageFrom.index);
  };

  const getAvailableStageFromOptions = (currentFundingStageType) => {
    if (currentFundingStageType?.value !== 'growth') {
      return fundOptions;
    }
    // For "from" selector in growth mode, limit to Series C+ (index 4) max
    return fundOptions.filter((option) => option.index <= 4);
  };

  return {
    formSchema,
    onSubmit,
    fundingStageTypeOptions,
    yearsOptions,
    fundOptions,
    fundingStageType,
    years,
    stageFrom,
    stageTo,
    onFundingStageTypeChange,
    onStageFromChange,
    setYears,
    setStageTo,
    getAvailableStageToOptions,
    getAvailableStageFromOptions,
    initialValues,
  };
};
